import first from 'lodash/first.js';
import get from 'lodash/get.js';
import has from 'lodash/has.js';

import axios from '@/plugins/axios.js';
import { promiseError, toggleLoading } from '@/store/utilities/Helpers.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

export default {
    state: {
        isEditing: false,
        isSaving: false,
        isDeleting: false,
        loading: false,
        grade: null,
        savedGrade: null,
        hasUnsavedChanges: false,
    },

    getters: {},

    actions: {
        async fetchGrade(context, { submissionId, courseId }) {
            try {
                toggleLoading(context);

                const queryBuilder = new QueryBuilder('gradebook-grades');

                queryBuilder.filter('activity_submission_id', submissionId).raw({ course_id: courseId });

                const { data } = await axios.get(queryBuilder.toUrl());

                const grade = first(data.data);

                await context.commit('SET_GRADE', grade);
                await context.commit('SET_SAVED_GRADE', grade);

                return grade;
            } catch (error) {
                toggleLoading(context);

                promiseError({
                    key: 'gradebook_grade_load_error',
                    context,
                });

                return error;
            }
        },

        editGrade({ commit }) {
            commit('TOGGLE_EDITING');
            commit('SET_GRADE', null);
        },

        cancelEdit({ state, commit }) {
            commit('TOGGLE_EDITING');
            commit('SET_GRADE', state.savedGrade);
        },

        async submitGrade(context, payload) {
            try {
                context.commit('TOGGLE_SAVING');

                const { data } = await axios.post('/api/gradebook-grades?include=media', payload);

                if (has(payload, 'activity_total')) data.data.activity_total = payload.activity_total;

                context.commit('SET_GRADE', data.data);
                context.commit('SET_SAVED_GRADE', data.data);

                context.dispatch('updateGradebook', {
                    activity_grade: data.data,
                });

                return data.data;
            } catch (e) {
                console.error(e);
                throw e;
            } finally {
                context.commit('TOGGLE_EDITING');

                context.commit('TOGGLE_SAVING');

                // context.commit('PURGE');
            }
        },

        async updateGrade({ state, commit, dispatch }, payload) {
            try {
                commit('TOGGLE_SAVING');

                const savedGrade = get(state, 'savedGrade.gradebook_grade_id') || get(state, 'savedGrade.id');

                const alternateSavedGrade = payload?.id;

                const { data } = await axios.put(
                    `/api/gradebook-grades/${alternateSavedGrade || savedGrade}?include=media`,
                    payload
                );

                if (has(payload, 'activity_total')) data.data.activity_total = payload.activity_total;

                commit('SET_GRADE', data.data);
                commit('SET_SAVED_GRADE', data.data);

                await dispatch('updateGradebook', { activity_grade: data.data });

                return data.data;
            } catch (e) {
                console.error(e);

                throw e;
            } finally {
                commit('TOGGLE_EDITING');

                commit('TOGGLE_SAVING');

                // commit('PURGE');
            }
        },

        async deleteGrade({ dispatch, commit, state }) {
            const grade = state.savedGrade || state.grade;

            if (has(grade, 'activity_submission_id') && !has(grade, 'gradebook_grade_id')) {
                grade.gradebook_grade_id = grade.id;
            }

            try {
                commit('TOGGLE_DELETING');

                return dispatch('Gradebook/deleteFromGradebook', { grade }, { root: true });
            } catch (e) {
                console.error(e);
                return e;
            } finally {
                commit('TOGGLE_EDITING');

                commit('TOGGLE_DELETING');

                commit('PURGE');
            }
        },

        async updateGradebook({ dispatch }, { activity_grade }) {
            dispatch('Gradebook/updateGradebook', { activity_grade }, { root: true });
        },
    },

    mutations: {
        TOGGLE_LOADING(state) {
            state.loading = !state.loading;
        },

        TOGGLE_SAVING(state) {
            state.isSaving = !state.isSaving;
        },

        TOGGLE_DELETING(state) {
            state.isDeleting = !state.isDeleting;
        },

        TOGGLE_EDITING(state) {
            state.isEditing = !state.isEditing;
        },

        SET_GRADE(state, grade) {
            state.grade = grade;
            state.hasUnsavedChanges = state.isEditing && !!grade;
        },

        SET_SAVED_GRADE(state, grade) {
            state.savedGrade = grade;
            state.hasUnsavedChanges = false;
        },

        DISCARD_CHANGES(state) {
            if (state.savedGrade) {
                state.grade = { ...state.savedGrade };
            } else {
                state.grade = null;
            }
            state.hasUnsavedChanges = false;
            state.isEditing = false;
        },

        PURGE(state) {
            state.isEditing = false;
            state.isSaving = false;
            state.isDeleting = false;
            state.loading = false;
            state.grade = null;
            state.savedGrade = null;
            state.hasUnsavedChanges = false;
        },
    },
};
